import { Box, Stack } from "@mui/material";
import React, { useCallback, useMemo } from "react";

import { SkeletonIndicator } from "components/common/AnimatedWebcam/SkeletonIndicator";
import { ProjectedAccuracy } from "components/detector/InfoChart/ProjectedMLAcc/ProjectedAccuracy";
import Histogram from "components/Histogram";
import { DROPPED_LABEL, NOT_YET_REVIEWED_LABEL } from "config/LabelMapping";
import { AnswerType, useTopLevelStats, type MetricClassCounts } from "hooks/DetectorDetail/useTopLevelStats";
import { Typography } from "new-components/typography/Typography";
import { DetectorModes } from "types/createDetector";
import { ANSWER_VALUES } from "types/imageQueries";
import { FilterOptionsLabel, getFilterOptions, redirectToDataPage } from "utils/redirectDataPage";

import { DetectorMetricsTable, DetectorMetricsTableRowData } from "../DetectorMetricsTable/DetectorMetricsTable";
import { DetectorTableCell } from "../DetectorMetricsTable/types";

import { type TopLevelStatsProps } from "./props";
import { StatsSelectors } from "./StatsSelectors";
import { getLowestGroundTruthClassCount, getTotalValue } from "./utils";

const cellClasses: Record<string, string> = {
  YES: "text-hl-purple",
  NO: "text-hl-magenta",
};

const METRICS_COLUMNS = ["YES", "NO"];
const COLUMNS = [...METRICS_COLUMNS, "UNCLEAR", "TOTAL"];
const UNCLEAR_COLUMNS = [
  ANSWER_VALUES.UNCLEAR,
  // eslint-disable-next-line no-underscore-dangle
  ANSWER_VALUES.__UNSURE,
  ANSWER_VALUES.NEEDS_REVIEW,
  DROPPED_LABEL,
  NOT_YET_REVIEWED_LABEL,
];

// eslint-disable-next-line prefer-arrow-callback
export const BinaryTopLevelStats = React.memo(function BinaryTopLevelStats(props: TopLevelStatsProps) {
  const { detectorData, confidenceThreshold, count } = props;
  const {
    answerType,
    handleActivityPeriodChange,
    handleAnswerTypeChange,
    histogramData,
    loadingMetrics,
    metrics,
    period,
    status,
  } = useTopLevelStats({ detectorData, confidenceThreshold, count });

  const getRedirect = useCallback(
    (label: FilterOptionsLabel, key?: string) =>
      redirectToDataPage({
        detectorId: detectorData.id,
        filterOptions: getFilterOptions({ label, answerType, value: key, period, mode: DetectorModes.BINARY }),
      }),
    [answerType, detectorData.id, period]
  );

  const getRowColumns = useCallback(
    (metric: MetricClassCounts, label: FilterOptionsLabel) => {
      if (!metric) return [];

      const columns: DetectorTableCell[] = METRICS_COLUMNS.map((key) => ({
        key,
        link: getRedirect(label, key),
        value: metric[key] ?? 0,
        className: cellClasses[key] ?? "text-neutral-400",
      }));

      const unclearValue = UNCLEAR_COLUMNS.reduce((sum, k) => sum + (metric[k] ?? 0), 0);
      columns.push({
        key: ANSWER_VALUES.UNCLEAR,
        link: getRedirect(label, ANSWER_VALUES.UNCLEAR),
        value: unclearValue,
        className: "text-neutral-400",
      });

      const totalValue = getTotalValue(metric);

      columns.push({
        key: "TOTAL",
        link: getRedirect(label, label === "Total Answered" ? "true" : undefined),
        value: totalValue,
      });
      return columns;
    },
    [getRedirect]
  );

  const generateValuesRow = useCallback(
    (label: FilterOptionsLabel, metric: MetricClassCounts) => ({
      label,
      cells: [...getRowColumns(metric, label)],
    }),
    [getRowColumns]
  );

  const rowsData: DetectorMetricsTableRowData[] = useMemo(() => {
    if (!metrics) return [];

    return [
      generateValuesRow("Answered By ML", metrics.summary.class_counts.source_ml),
      generateValuesRow("Answered By Cloud Labelers", metrics.summary.class_counts.cloud),
      generateValuesRow("Ground Truth", metrics.summary.class_counts.cust_labeler),
      {
        ...generateValuesRow("Total Answered", metrics.summary.class_counts.total),
        className: "border-b-2 border-gray-500 border-solid",
        textClass: "font-bold",
      },
      {
        label: answerType === AnswerType.FIRST_ANSWER ? "No First Answer" : "Not Answered",
        className: "border-b-2 border-gray-200 border-solid",
        textClass: "text-neutral-400",
        cell: {
          key: "Not Answered",
          value: metrics.summary.unconfident_counts?.total || 0,
          link: getRedirect("Not Answered", "false"),
        },
        colSpan: COLUMNS.length,
      },
      {
        label: "Total queries",
        cell: {
          key: "Total queries",
          value: metrics.summary.total_iqs || 0,
          link: redirectToDataPage({
            detectorId: detectorData.id,
            filterOptions: getFilterOptions({ period }),
          }),
        },
        className: "border-b-2 border-gray-500 border-solid",
        textClass: "font-bold",
        colSpan: COLUMNS.length,
      },
    ];
  }, [metrics, generateValuesRow, detectorData.id, answerType, getRedirect, period]);

  if (status) return status;

  return (
    <Stack className="w-full gap-4">
      {!detectorData ? (
        SkeletonIndicator({ loading: true, height: 58 })
      ) : (
          <ProjectedAccuracy
          /*      systemAccuracy={systemAccuracy} */
          totalImageQueries={detectorData.total_iqs || 0}
          detectorData={detectorData}
          lowestGroundTruthClassCount={getLowestGroundTruthClassCount(metrics)}
        />
      )}
      <StatsSelectors
        period={period}
        handleActivityPeriodChange={handleActivityPeriodChange}
        answerType={answerType}
        handleAnswerTypeChange={handleAnswerTypeChange}
      />
      {loadingMetrics ? (
        SkeletonIndicator({ loading: true, height: 290, className: "mt-4 mb-5" })
      ) : (
        <DetectorMetricsTable columns={COLUMNS} rowsData={rowsData} />
      )}
      {loadingMetrics ? (
        SkeletonIndicator({ loading: true, height: 190, className: "my-4" })
      ) : (
        <Box>
          <Typography variant="detail" textTransform="uppercase" align="center">
            First ML Confidence
          </Typography>
          <Box className="h-[170px] mt-2">
            <Histogram histogramData={histogramData} confidenceThreshold={confidenceThreshold} />
          </Box>
        </Box>
      )}
    </Stack>
  );
});
